import type { ZanshinStateValue } from '@/services/zanshin.service'
import {
  NoOpIcon, HedgeIcon, ShieldF1Icon, ShieldF2Icon,
  RunnerBrokerIcon, RunnerPropIcon, MomentumIcon,
} from '@/components/icons/ZanshinIcons'

/**
 * Mapping stato Zanshin → label/icona/colori. Single source of truth condiviso
 * tra ZanshinPairCard (vista completa) e ZanshinStateBadge (vista atomica inline).
 *
 * I colori sfondo/bordo/testo sono tinte scure tematiche per dark mode (Q3 spec).
 */
export interface StateMeta {
  label: string
  Icon: React.ComponentType<{ width?: number; height?: number; className?: string }>
  bg: string
  border: string
  text: string
}

export function getStateMeta(state: ZanshinStateValue | null, shieldPhase: number): StateMeta {
  if (state === null) return {
    // Slate medio (opzione B): si stacca dal blu-notte di sfondo, sembra "in pausa" non "vuoto"
    label: 'Nessuna operazione', Icon: NoOpIcon,
    bg: 'bg-[#1e293b]', border: 'border-[#475569] border-dashed', text: 'text-[#94a3b8]',
  }
  if (state === 'HEDGE') return {
    // Ambra: cautela difensiva
    label: 'Hedge attivo', Icon: HedgeIcon,
    bg: 'bg-[#1f1a00]', border: 'border-[#92400e]', text: 'text-[#fbbf24]',
  }
  if (state === 'BROKER_SHIELD' && shieldPhase === 1) return {
    // Blu: protezione fase iniziale
    label: 'Protezione F1', Icon: ShieldF1Icon,
    bg: 'bg-[#0d2a45]', border: 'border-[#1d4ed8]', text: 'text-[#bfdbfe]',
  }
  if (state === 'BROKER_SHIELD' && shieldPhase === 2) return {
    // Indaco: protezione profonda (distinto da F1 blu)
    label: 'Protezione F2', Icon: ShieldF2Icon,
    bg: 'bg-[#1e1b4b]', border: 'border-[#4338ca]', text: 'text-[#c7d2fe]',
  }
  if (state === 'RUNNER_BROKER') return {
    // Ciano vivace: release controllato sul broker (acqua = sicurezza fluida)
    label: 'Runner broker', Icon: RunnerBrokerIcon,
    bg: 'bg-[#0c4a6e]', border: 'border-[#06b6d4]', text: 'text-[#67e8f9]',
  }
  if (state === 'RUNNER_PROP') return {
    // Smeraldo: profitto in corsa sul prop (verde "soldi")
    label: 'Runner prop', Icon: RunnerPropIcon,
    bg: 'bg-[#022c22]', border: 'border-[#10b981]', text: 'text-[#a7f3d0]',
  }
  if (state === 'RUNNER_ATTIVO') return {
    // Arancione: il fuoco — massima accelerazione
    label: 'Momentum attivo', Icon: MomentumIcon,
    bg: 'bg-[#431407]', border: 'border-[#ea580c]', text: 'text-[#fed7aa]',
  }
  return {
    label: state, Icon: NoOpIcon,
    bg: 'bg-[#1a1a1a]', border: 'border-[#2a2a2a]', text: 'text-[#9ca3af]',
  }
}
